import { useState, useLayoutEffect, useEffect, useMemo } from 'react'

import {
  AudioObjectPaginatedList,
  AudiosAudioIdDeleteRequest,
  AudiosGetRequest,
} from '@nativewaves/nw-cloud-sdk/content'
import { useQuery, useInfiniteQuery, useMutationState, InfiniteData, useQueryClient } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'
import { pickBy } from 'lodash-es'

import { audioObjectMutationKeys } from 'hooks/mutations/content'
import { audioObjectsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Audio/Objects'
import { audioObjectQueryKeys, workspaceQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { mergeBatchIntoSource } from 'utils'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ virtualItems, setLoadingRows }: UseAsyncContentCaching) => {
  const queryClient = useQueryClient()

  const { workspaceId } = audioObjectsOverviewRoute.useParams()
  const search = audioObjectsOverviewRoute.useSearch()
  const { query, timeRangeStart, timeRangeEnd } = search

  const sortProperties = useMemo(
    () => pickBy(search, (_, key) => key.startsWith('orderBy')) as PickStartsWith<AudiosGetRequest, 'orderBy'>,
    [search],
  )

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const audiosQuery = useInfiniteQuery({
    ...audioObjectQueryKeys.list({
      repositoryId: workspaceQuery.data?.contentRepositoryId,
      query,
      timeRangeStart,
      timeRangeEnd,
      ...sortProperties,
    }),
    enabled: workspaceQuery.isSuccess,
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [batchGetEnabled, setBatchGetEnabled] = useState(audiosQuery.isSuccess)

  const pendingAudioObjects = useMutationState({
    filters: { mutationKey: audioObjectMutationKeys.delete(), status: 'pending' },
    select: (mutation) => mutation.state.variables as AudiosAudioIdDeleteRequest,
  })

  const audioBatchQuery = useQuery({
    ...audioObjectQueryKeys.batchGet(
      {},
      () => virtualItems?.map((virtualItem) => audiosQuery.data?.flat[virtualItem.index]?.id) as string[],
    ),
    enabled: batchGetEnabled,
    refetchInterval: RefetchIntervals.NORMAL,
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useLayoutEffect(() => {
    if (audiosQuery.isSuccess && audioBatchQuery.isSuccess) {
      queryClient.setQueriesData<InfiniteData<AudioObjectPaginatedList>>(
        { queryKey: audioObjectQueryKeys.lists() },
        (old) => {
          if (old) {
            return {
              ...old,
              pages:
                old.pages.map((page) => ({
                  ...page,
                  items: mergeBatchIntoSource(
                    page.items,
                    audioBatchQuery.data.items,
                    new Set(audioBatchQuery.data.notFoundIds),
                  ),
                })) ?? [],
            }
          }
          return old
        },
      )
    }
  }, [
    audioBatchQuery.data?.items,
    audioBatchQuery.data?.notFoundIds,
    audioBatchQuery.isSuccess,
    audiosQuery.isSuccess,
    queryClient,
  ])

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingAudioObjects.map((task) => task.audioId)])])
  }, [pendingAudioObjects, setLoadingRows])

  useEffect(() => {
    if (audiosQuery.isSuccess) {
      if (!batchGetEnabled) {
        const timeoutHandle = setTimeout(() => {
          setBatchGetEnabled(true)
          clearTimeout(timeoutHandle)
        }, 5000)
      }
    }
  }, [batchGetEnabled, audiosQuery.isSuccess])

  return {
    workspaceQuery,
    audiosQuery,
    setBatchGetEnabled,
  }
}
